import React, { useEffect } from 'react';
import { formatPrice } from '../constants/reviewVariants.js';

function CalendarIcon() {
  return (
    <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
    </svg>
  );
}

function GraduationCapIcon() {
  return (
    <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-5 w-5 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 5.25-4.5 9-9 9s-9-3.75-9-9 4.5-9 9-9 9 3.75 9 9Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5 text-green-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2Z" />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v3m-3.75-3h7.5M12 3a6 6 0 0 0-3.5 10.88V15a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-1.12A6 6 0 0 0 12 3Z" />
    </svg>
  );
}

const FEATURE_BADGE_VARIANTS = {
  yellow: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  gray: 'bg-gray-200 text-gray-800',
};

function PriceSkeleton() {
  return (
    <div className="mt-3 flex items-baseline gap-2">
      <div className="h-7 w-20 rounded bg-gray-200 animate-pulse" />
      <div className="h-3 w-16 rounded bg-gray-100 animate-pulse" />
    </div>
  );
}

function FeatureRow({ feature }) {
  const included = feature.included !== false;
  return (
    <li className="flex items-start gap-2 text-sm">
      {included ? <CheckIcon /> : <XIcon />}
      <span className={included ? 'text-gray-900' : 'text-gray-500'}>
        <span className={included ? 'font-semibold' : ''}>{feature.text}</span>
        {feature.detail && (
          <span className={included ? 'text-gray-500' : ''}> — {feature.detail}</span>
        )}
        {feature.badge && (
          <span className={`ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${FEATURE_BADGE_VARIANTS[feature.badgeVariant] || FEATURE_BADGE_VARIANTS.gray}`}>
            {feature.badge}
          </span>
        )}
      </span>
    </li>
  );
}

function PlanCard({
  planValue,
  selected,
  onSelect,
  icon,
  header,
  subheader,
  switchLink,
  unitCents,
  priceSuffix,
  bestValue,
  savingsBadge,
  featuresLabel,
  features,
  callout,
  volumeSavings,
  loading,
}) {
  const hasFeatures = Array.isArray(features) && features.length > 0;
  const priceColor = hasFeatures ? 'text-gray-900' : 'text-indigo-600';
  return (
    <label className={`relative block cursor-pointer rounded-lg border-2 p-5 transition-colors ${selected ? 'border-indigo-600 bg-indigo-50/40' : 'border-gray-200 hover:border-indigo-300'}`}>
      {bestValue && hasFeatures && (
        <span className="absolute -top-3 right-4 inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1 text-xs font-semibold text-white shadow">
          <StarIcon />
          BEST VALUE
        </span>
      )}
      <div className="flex items-start gap-4">
        <input
          type="radio"
          name="plan"
          value={planValue}
          checked={selected}
          onChange={onSelect}
          className="mt-1 h-4 w-4 text-indigo-600"
        />
        {!hasFeatures && icon && <div className="shrink-0">{icon}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900">{header}</h3>
            {bestValue && !hasFeatures && (
              <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                Best Value
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600">{subheader}</p>
          {switchLink && (
            <p className="mt-1 text-sm">
              <a href={switchLink.to} className="font-medium text-indigo-600 hover:text-indigo-700 underline" onClick={(e) => e.stopPropagation()}>
                {switchLink.label}
              </a>
            </p>
          )}

          {loading ? (
            <PriceSkeleton />
          ) : (
            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              <span className={`text-3xl font-bold leading-none ${priceColor}`}>{formatPrice(unitCents)}</span>
              {priceSuffix && <span className="text-sm text-gray-500">{priceSuffix}</span>}
              {savingsBadge && (
                <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                  {savingsBadge}
                </span>
              )}
            </div>
          )}

          {hasFeatures && (
            <div className="mt-4 space-y-3">
              {featuresLabel && (
                <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase">{featuresLabel}</p>
              )}
              <ul className="space-y-2">
                {features.map((f, i) => <FeatureRow key={i} feature={f} />)}
              </ul>
              {callout && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-gray-700">
                  <LightbulbIcon />
                  <span>{callout}</span>
                </div>
              )}
            </div>
          )}

          {volumeSavings && (
            <div className="mt-3 flex items-center gap-2 text-xs font-medium text-indigo-700">
              <ShieldIcon />
              {volumeSavings}
            </div>
          )}
        </div>
      </div>
    </label>
  );
}

function resolveSubheader(subheader, ctx) {
  return typeof subheader === 'function' ? subheader(ctx) : subheader;
}

function computeSavingsBadge(subCents, otCents) {
  if (subCents == null || otCents == null) return null;
  const diff = otCents - subCents;
  if (diff <= 0) return null;
  return `SAVE ${formatPrice(diff)}`;
}

export default function ReviewCards({
  variant,
  pricing,
  loading,
  selectedPlan,
  onSelectPlan,
  complianceText,
  facilityCode,
  subscriptionVolumeSavings,
  oneTimeVolumeSavings,
}) {
  const subAvailable = !!pricing?.subscription && !!variant?.subscription;
  const otAvailable = !!pricing?.one_time && !!variant?.oneTime;
  const isLoading = loading || !pricing;
  const ctx = { facilityCode };
  const subHasFeatures = Array.isArray(variant?.subscription?.features) && variant.subscription.features.length > 0;
  const savingsBadge = !isLoading && subAvailable && otAvailable && subHasFeatures
    ? computeSavingsBadge(pricing.subscription.unit_cents, pricing.one_time.unit_cents)
    : null;

  useEffect(() => {
    if (isLoading) return;
    if (selectedPlan === 'subscription' && !subAvailable && otAvailable) onSelectPlan('one_time');
    else if (selectedPlan === 'one_time' && !otAvailable && subAvailable) onSelectPlan('subscription');
  }, [isLoading, selectedPlan, subAvailable, otAvailable, onSelectPlan]);

  return (
    <div className="space-y-4">
      {subAvailable && (
        <PlanCard
          planValue="subscription"
          selected={selectedPlan === 'subscription'}
          onSelect={() => onSelectPlan('subscription')}
          icon={<CalendarIcon />}
          header={variant.subscription.header}
          subheader={resolveSubheader(variant.subscription.subheader, ctx)}
          switchLink={variant.subscription.switchLink}
          unitCents={pricing.subscription.unit_cents}
          priceSuffix={variant.subscription.priceSuffix}
          bestValue
          savingsBadge={savingsBadge}
          featuresLabel={variant.subscription.featuresLabel}
          features={variant.subscription.features}
          callout={variant.subscription.callout}
          volumeSavings={subscriptionVolumeSavings}
          loading={isLoading}
        />
      )}
      {otAvailable && (
        <PlanCard
          planValue="one_time"
          selected={selectedPlan === 'one_time'}
          onSelect={() => onSelectPlan('one_time')}
          icon={<GraduationCapIcon />}
          header={variant.oneTime.header}
          subheader={resolveSubheader(variant.oneTime.subheader, ctx)}
          switchLink={variant.oneTime.switchLink}
          unitCents={pricing.one_time.unit_cents}
          priceSuffix={variant.oneTime.priceSuffix}
          featuresLabel={variant.oneTime.featuresLabel}
          features={variant.oneTime.features}
          callout={variant.oneTime.callout}
          volumeSavings={oneTimeVolumeSavings}
          loading={isLoading}
        />
      )}
      {!isLoading && !subAvailable && !otAvailable && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900">
          This training is not currently available for your facility type. Please contact support.
        </div>
      )}
      {complianceText && (
        <div className="flex items-start gap-2 px-1 pt-1 text-xs text-gray-500">
          <ShieldIcon />
          <p>{complianceText}</p>
        </div>
      )}
    </div>
  );
}
